import CheckIcon from "@mui/icons-material/Check";
import NearMeIcon from "@mui/icons-material/NearMe";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Portal from "@mui/material/Portal";
import Stack from "@mui/material/Stack";
import { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { BatteryRunawayPhase } from "../hooks/useBatteryRunawayAlarm";

export interface BatteryRunawayAlarmProps {
  phase: BatteryRunawayPhase;
  muted: boolean;
  deviceName: string | null;
  onMute: () => void;
  onGoToDevice: () => void;
  onAcknowledge: () => void;
}

// Alternating red/white ↔ white/red. steps(1) makes it a hard cut, not a fade — maximally jarring.
const flashKeyframes = {
  "@keyframes brFlash": {
    "0%": { backgroundColor: "#d32f2f", color: "#ffffff" },
    "50%": { backgroundColor: "#ffffff", color: "#d32f2f" },
    "100%": { backgroundColor: "#d32f2f", color: "#ffffff" },
  },
};

const overlayZ = (theme: Theme) => theme.zIndex.modal + 100;

const actionButtonSx: SxProps<Theme> = {
  bgcolor: "rgba(0,0,0,0.85)",
  color: "#ffffff",
  fontSize: "1.25rem",
  px: 4,
  py: 1.5,
  "&:hover": { bgcolor: "#000000" },
  "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.4)", color: "rgba(255,255,255,0.6)" },
};

export default function BatteryRunawayAlarm(props: BatteryRunawayAlarmProps) {
  const { phase, muted, deviceName, onMute, onGoToDevice, onAcknowledge } = props;
  const { t } = useTranslation();

  if (phase === null) return null;

  const vehicleLine = `${t("batteryRunaway.vehicle")}: ${deviceName ?? "—"}`;

  // Portal to document.body so the fixed overlay is positioned against the viewport. A
  // transformed/contained ancestor in the page tree would otherwise become the containing
  // block, shifting the banner partly off-screen.
  if (phase === "banner") {
    return (
      <Portal>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            maxWidth: "100vw",
            boxSizing: "border-box",
            zIndex: overlayZ,
            minHeight: 72,
            px: 3,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1,
            boxShadow: 6,
            animation: "brFlash 0.8s steps(1, end) infinite",
            ...flashKeyframes,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              maxWidth: "100%",
            }}
          >
            <WarningAmberIcon sx={{ fontSize: "2.5rem", flexShrink: 0 }} />
            <Typography sx={{ fontWeight: 900, fontSize: "1.5rem" }}>
              {t("batteryRunaway.title")}
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>{vehicleLine}</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={onAcknowledge}
            sx={{ ...actionButtonSx, fontSize: "1rem", py: 0.75 }}
          >
            {t("batteryRunaway.acknowledge")}
          </Button>
        </Box>
      </Portal>
    );
  }

  return (
    <Portal>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          maxWidth: "100vw",
          maxHeight: "100vh",
          boxSizing: "border-box",
          zIndex: overlayZ,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 2, sm: 4 },
          p: 3,
          textAlign: "center",
          overflow: "auto",
          animation: "brFlash 0.6s steps(1, end) infinite",
          ...flashKeyframes,
        }}
      >
        <WarningAmberIcon sx={{ fontSize: "clamp(2.5rem, 9vh, 7rem)" }} />
        <Typography
          component="div"
          sx={{
            fontSize: "clamp(2rem, min(11vw, 12vh), 7rem)",
            fontWeight: 900,
            lineHeight: 1,
            letterSpacing: 2,
          }}
        >
          {t("batteryRunaway.title")}
        </Typography>

        <Typography sx={{ fontSize: "clamp(1.1rem, 3.5vh, 2.5rem)", fontWeight: 700 }}>
          {vehicleLine}
        </Typography>

        <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", justifyContent: "center", gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<VolumeOffIcon />}
            onClick={onMute}
            disabled={muted}
            sx={actionButtonSx}
          >
            {t("batteryRunaway.mute")}
          </Button>
          <Button
            variant="contained"
            startIcon={<NearMeIcon />}
            onClick={onGoToDevice}
            sx={actionButtonSx}
          >
            {t("batteryRunaway.goToDevice")}
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={onAcknowledge}
            sx={actionButtonSx}
          >
            {t("batteryRunaway.acknowledge")}
          </Button>
        </Stack>
      </Box>
    </Portal>
  );
}
